import {Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FileCheckService} from '@services/file-check.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ImageUploadService} from '@services/image-upload.service';
import {ImageResource} from '@services/resource/image-resource';
import {ImageType} from '@services/resource/image-type';
import {ContinuousActionComponent} from '@widgets/continuous-action/continuous-action.component';
import {combineLatest, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {ClientConfig} from '@app/client-config';
import {CommonModule} from '@angular/common';
import {MarkantElementsModule} from '@markant/elements-angular';
import {DropzoneComponent} from '@dashboard/dropzone/dropzone.component';
import {UploadImageComponent} from '@dashboard/upload-image/upload-image.component';

@Component({
  selector: 'app-media-list',
  templateUrl: './media-list.component.html',
  styleUrls: ['./media-list.component.scss'],
  standalone: true,
  imports: [CommonModule, MarkantElementsModule, ContinuousActionComponent, DropzoneComponent, UploadImageComponent],
})
export class MediaListComponent implements OnInit {
  @Input() productId: string;
  @ViewChild('FileInput') fileInput: ElementRef;
  @ViewChildren('productTiles') productTiles: QueryList<HTMLDivElement>;
  @Output() mediaListChanged = new EventEmitter<ImageResource[]>();
  @Input() mediaList: ImageResource[];
  @Input() disabled = false;
  @Input() originId: string | null;
  @Input() startupName: string | null = null;
  @Input() productName: string | null = null;
  @Input() productCategory: string | null = null;
  @Output() updatedCoverImage = new EventEmitter<[Blob, string]>();

  @ViewChild(ContinuousActionComponent, {static: true})
  continuous: ContinuousActionComponent;
  imageType = ImageType.COVER;
  dialogState = false;
  pickedImage: string | null = null;
  imageUrls: string[];
  deleteTextSelected = 'Stelle zuerst ein anderes Hauptbild ein bevor du dieses löscht';
  deleteTextNotSelected = 'Produktbild löschen';

  private readonly newFiles$: Subject<FileList> = new Subject();

  public readonly maxImageSize = ClientConfig.maxImageSize;
  public selectedCoverImage: ImageResource | undefined = undefined;

  constructor(
    private readonly fileCheckService: FileCheckService,
    private readonly snackBar: MatSnackBar,
    private readonly imageUploadService: ImageUploadService,
  ) {}

  ngOnInit() {
    this.newFiles$
      .pipe(
        map((fs) => Array.from(fs)),
        map((fs) => this.validateFiles(fs)),
        this.continuous.actions((fs) =>
          combineLatest(fs.map((f) => this.imageUploadService.uploadImage(this.productId, f, new ImageResource(ImageType.LIST)))),
        ),
      )
      .subscribe(
        (is: ImageResource[]) => {
          if (is.length == 1 && this.originId == null) {
            this.selectCover(is[0]);
          }
          is.forEach((i) => this.mediaList.push(i));
          this.mediaListChanged.emit(this.mediaList);
        },
        (error) => {
          this.snackBar.open(error.message, 'Schließen', {duration: 6000});
        },
      );
  }

  private validateFiles(fs: File[]): File[] {
    return fs.filter((f) => {
      const errors = this.fileCheckService.checkFile(f, ['image/png', 'image/jpeg'], ClientConfig.maxImageSize);
      errors.forEach((e) => this.snackBar.open(e.message, 'Schließen', {duration: 6000}));
      return errors.length === 0;
    });
  }

  remove(imageResource: ImageResource, i: any): void {
    if (this.disabled) {
      return;
    }
    this.mediaList.splice(i, 1);
    this.mediaListChanged.emit(this.mediaList);
  }

  down(imageResource: ImageResource, i: any): void {
    if (this.disabled) {
      return;
    }
    if (i + 1 < this.mediaList.length) {
      const temp = this.mediaList[i + 1];
      this.mediaList[i + 1] = this.mediaList[i];
      this.mediaList[i] = temp;
      this.mediaListChanged.emit(this.mediaList);
    }
  }

  up(imageResource: ImageResource, i: any) {
    if (this.disabled) {
      return;
    }
    if (i > 0) {
      const temp = this.mediaList[i - 1];
      this.mediaList[i - 1] = this.mediaList[i];
      this.mediaList[i] = temp;
      this.mediaListChanged.emit(this.mediaList);
    }
  }

  /** The change event on the input will not trigger if a File is added, removed and added again unless it's value will is reset */
  resetFileInput() {
    this.fileInput.nativeElement.value = null;
  }

  handleNewFiles(files: FileList) {
    if (files.length < 1) {
      return;
    }
    this.newFiles$.next(files);
  }

  isImage(media: any): boolean {
    // SINCE instanceof is **** in typescript we have to create a 'typeguard'
    // https://www.typescriptlang.org/docs/handbook/advanced-types.html#type-guards-and-differentiating-types
    return media.imageType !== undefined;
  }

  openModal(url: string) {
    this.dialogState = true;
    this.pickedImage = url;
    this.imageUrls = this.mediaList.map((image) => image.uri!);
  }

  closeModal() {
    this.dialogState = false;
    this.pickedImage = null;
  }

  before() {
    if (this.indexOfCurrentUrl === 0) {
      this.pickedImage = this.imageUrls[this.imageUrls.length - 1];
    } else {
      this.pickedImage = this.imageUrls[this.indexOfCurrentUrl - 1];
    }
  }

  next() {
    if (this.indexOfCurrentUrl === this.imageUrls.length - 1) {
      this.pickedImage = this.imageUrls[0];
    } else {
      this.pickedImage = this.imageUrls[this.indexOfCurrentUrl + 1];
    }
  }

  get indexOfCurrentUrl() {
    return this.imageUrls.indexOf(this.pickedImage!);
  }

  isCoverImage(image: ImageResource) {
    return image.id === this.originId;
  }

  cancelUpload() {
    this.selectedCoverImage = undefined;
  }
  selectCover(image: ImageResource) {
    this.selectedCoverImage = image;
  }
  updateCoverImage(image: Blob) {
    this.updatedCoverImage.emit([image, this.selectedCoverImage!.id!]);
    this.selectedCoverImage = undefined;
  }
}
